<script setup lang="ts">
  import type { AgencyRef } from '@gat-ship/types'
  import type {
    ComboboxFieldItem,
    ComboboxFieldProps
  } from '@gat-ship/vue-components'
  import { computed, onMounted, ref } from 'vue'
  import { testIds } from '@gat-ship/types'
  import { useAgencyRefDataSource } from '@gat-ship/ref-data'
  import { ComboboxField } from '@gat-ship/vue-components'
  export interface SelectAgencyProps extends Omit<
    ComboboxFieldProps,
    'items' | 'modelValue'
  > {
    items?: AgencyRef[]
    showInactiveAgencies?: boolean
    preSelectedAgency?: AgencyRef
    /** Whether to fetch the agencies on mount. @default true */
    fetchOnMount?: boolean
  }

  const props = defineProps<SelectAgencyProps>()

  const emit = defineEmits<{
    (e: 'agencies-fetched', payload: AgencyRef[]): void
  }>()

  // Shared agency ref singleton (replaces the retired RefDataProvider); it owns
  // its own SignalR subscription, so this component no longer manages it.
  const { agencies, fetchAgencies } = useAgencyRefDataSource()

  onMounted(async () => {
    if (props.fetchOnMount ?? true) {
      await fetchAgencies()
      if (agencies.value) {
        emit('agencies-fetched', agencies.value)
      }
    }
  })

  const selectedAgencyId = defineModel<AgencyRef['id'] | undefined>({
    required: true
  })

  const isOpen = defineModel<boolean>('open', { default: false })

  function getTitle(item: AgencyRef) {
    if (
      (agencies.value?.filter((i) => i.name === item.name) ?? []).length > 1
    ) {
      return {
        name: item.name,
        shortName: item.shortName
      }
    }
    return { name: item.name }
  }

  const preSelectedAgencyRef = ref<AgencyRef | undefined>(undefined)

  const _items = computed(() => {
    const result: ComboboxFieldItem<AgencyRef>[] =
      agencies.value?.map((i) => ({
        title: i.name,
        value: i.id,
        searchValue: `${i.name} ${i.shortName}`,
        metaData: i
      })) ?? []
    const selectedAgencyRef =
      preSelectedAgencyRef.value || props.preSelectedAgency
    if (
      selectedAgencyRef &&
      !result.find((i) => i.value === selectedAgencyRef.id)
    ) {
      result.push({
        title: selectedAgencyRef.name,
        value: selectedAgencyRef.id,
        searchValue: `${selectedAgencyRef.name} ${selectedAgencyRef.shortName}`,
        metaData: selectedAgencyRef
      })
    }
    return result.sort((a, b) => a.title.localeCompare(b.title))
  })

  defineExpose({
    fetchAgenciesRef: fetchAgencies,
    setPreSelectedAgency: (agency: AgencyRef) => {
      preSelectedAgencyRef.value = agency
    }
  })
</script>
<template>
  <ComboboxField
    v-bind="props"
    v-model="selectedAgencyId"
    v-model:open="isOpen"
    :data-testid="props.dataTestid ?? testIds.common.select.agency"
    :items="_items"
    @update:open.once="
      () => {
        fetchAgencies()
      }
    "
  >
    <template v-if="$slots.trigger" #trigger>
      <slot name="trigger" />
    </template>
    <template #item="{ item }">
      <div class="flex grow items-center">
        <div>{{ getTitle(item.metaData).name }}</div>
      </div>
    </template>
  </ComboboxField>
</template>
